import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, FileJson, Hourglass } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { formatBytes } from '@/features/objects/utils/formatSize'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { useTimezoneDate } from '@/hooks/useTimezoneDate'
import { tierLabel } from '../lib/tier-label'
import type { RecoveryChainVersion } from '../api'

interface RecoveryChainDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectId: string
    versions: RecoveryChainVersion[]
    isLoading: boolean
}

/**
 * The versions this recovery restores, in the order they are restored: boot, full, incrementals,
 * logs. It uses the same columns as the Objects screen's recovery chain, plus when each backup was
 * taken and whether its copy to egress has landed.
 *
 * The rows are the recovery's own, fixed when it was requested. They are not the object's current
 * chain as the Objects screen shows it. That chain moves on as new backups land and shows only the
 * primary chain, while a recovery built from the last clean chain restores different versions.
 */
export function RecoveryChainDialog({ open, onOpenChange, objectId, versions, isLoading }: RecoveryChainDialogProps) {
    const { t } = useTranslation()
    const { formatDateShort } = useTimezoneDate()
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string } | null>(null)


    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1000px]">
                    <DialogHeader>
                        <DialogTitle>
                            {t('objects.recoveryChain', 'Recovery Chain')} — {objectId}
                        </DialogTitle>
                        <DialogDescription>
                            {t('recoverRequests.recoveryChainDescription', 'The versions this recovery restores, in the order they are restored.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4">
                        {isLoading ? (
                            <div className="flex h-32 items-center justify-center text-muted-foreground">
                                {t('recoverRequests.loading', 'Loading...')}
                            </div>
                        ) : versions.length === 0 ? (
                            <div className="flex h-32 items-center justify-center text-muted-foreground">
                                {t('recoverRequests.noRecoveryChain', 'This recovery restores no backup versions.')}
                            </div>
                        ) : (
                            // A chain is a handful of versions: a plain table, with no paging or column
                            // chooser. Version names are long and unbroken, so they wrap in their own
                            // column rather than running into the next one.
                            <div className="overflow-x-auto rounded-md border">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                        <tr className="border-b text-left">
                                            <th className="px-3 py-2 font-medium">{t('recoverRequests.tier', 'Tier')}</th>
                                            <th className="px-3 py-2 font-medium">{t('recoverRequests.chainVersion', 'Version')}</th>
                                            <th className="whitespace-nowrap px-3 py-2 font-medium">{t('recoverRequests.chainBackedUp', 'Backed up')}</th>
                                            <th className="px-3 py-2 text-center font-medium">{t('recoverRequests.analytics', 'Analytics')}</th>
                                            <th className="px-3 py-2 text-right font-medium">{t('recoverRequests.size', 'Size')}</th>
                                            <th className="px-3 py-2 font-medium">{t('recoverRequests.chainEgress', 'Egress')}</th>
                                            <th className="px-3 py-2 text-center font-medium">{t('recoverRequests.metadata', 'Metadata')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {versions.map((v) => (
                                            <tr key={v.recoverRequestId} className="border-b align-top last:border-b-0">
                                                <td className="px-3 py-2">
                                                    <Badge variant="outline" className="px-1.5 py-0 text-[11px] font-semibold">
                                                        {tierLabel(t, v.tier)}
                                                    </Badge>
                                                </td>
                                                <td className="min-w-[12rem] break-all px-3 py-2 font-mono text-xs">{v.objectName}</td>
                                                <td className="whitespace-nowrap px-3 py-2">{v.datetime ? formatDateShort(v.datetime) : '—'}</td>
                                                <td className="px-3 py-2">
                                                    <div className="flex justify-center">
                                                        <AnalyticsStatusIndicator status={v.analytics} size="h-4 w-4" />
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-right">{v.size != null ? formatBytes(v.size) : '—'}</td>
                                                <td className="px-3 py-2">
                                                    {v.inEgress ? (
                                                        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-emerald-600">
                                                            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                                                            {t('recoverRequests.chainInEgress', 'In egress')}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-amber-600">
                                                            <Hourglass className="h-3.5 w-3.5" aria-hidden="true" />
                                                            {t('recoverRequests.chainCopying', 'Copying to egress')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    {v.metadata ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 px-1"
                                                            onClick={() => setJsonDialog({ title: `${t('recoverRequests.metadata', 'Metadata')} — ${v.objectName}`, content: v.metadata! })}
                                                            aria-label={t('recoverRequests.metadata', 'Metadata')}
                                                        >
                                                            <FileJson className="h-3.5 w-3.5" />
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted-foreground">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {jsonDialog && (
                <JsonViewDialog
                    open={!!jsonDialog}
                    onOpenChange={(o) => { if (!o) setJsonDialog(null) }}
                    title={jsonDialog.title}
                    content={jsonDialog.content}
                />
            )}
        </>
    )
}
